import type { FormikErrors } from 'formik'
import type { ICustomFieldValueFormModel } from './typings'
import type { ICustomFieldFormSetting } from '@user/store/entityFormSettings'

import {
  isValidMuiDate,
  objectKeys,
  setDeepNestedProperty,
} from '@sales-center/common'

export function validateCustomFields<
  FormModel extends ICustomFieldValueFormModel,
>(
  model: FormModel,
  customFieldSettings: ICustomFieldFormSetting[],
): FormikErrors<FormModel> {
  const errors: FormikErrors<FormModel> = {}

  const { customFields } = model

  objectKeys(customFields).map((fieldType) => {
    const fields = customFields[fieldType]

    Object.keys(fields).map((fieldId) => {
      const customFieldId = Number(fieldId)
      const value = fields[customFieldId].value

      const setting = customFieldSettings.find(
        (x) => x.customFieldId === Number(customFieldId),
      )

      if (!!setting) {
        const { required } = setting

        const isDefaultValue = checkForDefaultValue(value)

        let errorText: string | undefined

        if (isDefaultValue && required) {
          errorText = 'Required'
        } else if (fieldType === 'date') {
          if (!isDefaultValue && !isValidMuiDate(value as string | Date | null))
            errorText = 'Invalid date format'
        } else if (fieldType === 'number') {
          errorText = validateNumber(value as string | null, isDefaultValue)
        }

        if (!!errorText) {
          setDeepNestedProperty(
            errors,
            [`customFields`, fieldType, customFieldId],
            errorText,
            true,
          )
        }
      }
    })
  })

  return errors
}

function validateNumber(value: string | null, isDefaultValue: boolean) {
  if (!isDefaultValue && isNaN(Number(value))) {
    return 'Must be a number'
  }

  if (typeof value === 'string') {
    if (value.replace(/[^\d]/g, '').length > 16) {
      return 'Must be no more than 16 digits'
    }
  }
}

function checkForDefaultValue(value: unknown) {
  if (value === null) return true
  if (value === undefined) return true
  if (value === false) return true
  if (typeof value === 'string') return value === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}
